// stores/consentStore.ts
import { create } from "zustand";
import { persist } from "zustand/middleware";

interface ConsentState {
  // State
  hasGivenConsent: boolean;
  consentTimestamp: string | null;
  hasHydrated: boolean;

  // Actions
  setConsent: (consent: boolean) => void;
  checkConsent: () => boolean;
  clearConsent: () => void;
  getConsentAge: () => number; // Días desde el consentimiento
  syncWithStorage: () => void;
}

export const useConsentStore = create<ConsentState>()(
  persist(
    (set, get) => ({
      // Initial state
      hasGivenConsent: false,
      consentTimestamp: null,
      hasHydrated: false,

      // Set consentimiento
      setConsent: (consent: boolean) => {
        set({
          hasGivenConsent: consent,
          consentTimestamp: consent ? new Date().toISOString() : null,
        });
      },

      // Check if user has given consent
      checkConsent: () => {
        const state = get();

        const zustandStorage = localStorage.getItem("consent-storage");
        let Consentimiento = null;

        if (zustandStorage) {
          try {
            const parsedStorage = JSON.parse(zustandStorage);
            Consentimiento = parsedStorage.state?.hasGivenConsent || null;
          } catch {
            // Si hay error parseando, limpiar todo
            localStorage.removeItem("consent-storage");
            set({
              hasGivenConsent: false,
              consentTimestamp: null,
              hasHydrated: false,
            });
            return false;
          }
        }

        if (!zustandStorage && state.hasGivenConsent) {
          set({
            hasGivenConsent: false,
            consentTimestamp: null,
            hasHydrated: false,
          });
          return false;
        }

        if (!state.consentTimestamp && state.hasGivenConsent) {
          set({
            hasGivenConsent: false,
            consentTimestamp: null,
            hasHydrated: false,
          });
          return false;
        }

        const isConsent = !!(state.hasGivenConsent && state.consentTimestamp);

        if (state.hasGivenConsent !== true) {
          set({ hasGivenConsent: isConsent });
        }

        return isConsent;
      },

      // Clear consent
      clearConsent: () => {
        set({
          hasGivenConsent: false,
          consentTimestamp: null,
        });
      },

      // Get consent age in days
      getConsentAge: () => {
        const state = get();
        if (!state.consentTimestamp) return 0;

        const consentDate = new Date(state.consentTimestamp);
        const today = new Date();
        const diffTime = Math.abs(today.getTime() - consentDate.getTime());
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      },
      syncWithStorage: () => {
        const zustandStorage = localStorage.getItem("consent-storage");

        if (!zustandStorage) {
          set({
            hasGivenConsent: false,
            consentTimestamp: null,
          });
          return;
        }

        try {
          const parsedStorage = JSON.parse(zustandStorage);
          const storageConsent = parsedStorage.state?.hasGivenConsent;
          const storageTimestamp = parsedStorage.state?.consentTimestamp;

          const currentState = get();

          // Solo actualizar si hay diferencias
          if (
            currentState.hasGivenConsent !== storageConsent ||
            currentState.consentTimestamp !== storageTimestamp
          ) {
            set({
              hasGivenConsent: storageConsent || false,
              consentTimestamp: storageTimestamp || null,
            });
          }
        } catch (error) {
          console.error("Error syncing consent store:", error);
          // En caso de error, limpiar todo
          set({
            hasGivenConsent: false,
            consentTimestamp: null,
          });
        }
      },
    }),
    {
      name: "consent-storage",
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.hasHydrated = true;
        }
      },
    }
  )
);
